import {
  IMergedRateCard,
  IRateCard,
  IRateValue
} from "@wingspanhq/payments/dist/interfaces";
import { RateCardEditFormData } from "./types";

/**
 * Checks if a custom rate card ID exists on the merged rate card
 * Note: Backend uses 'payeeEngagementRateCardId' but interface defines 'payerPayeeEngagementRateCardId'
 * Checking both for compatibility
 */
export const hasCustomRateCardId = (
  mergedRateCard: IMergedRateCard | null
): boolean => {
  return !!(
    mergedRateCard?.payerPayeeEngagementRateCardId ||
    (mergedRateCard as any)?.payeeEngagementRateCardId
  );
};

/**
 * Determines if a rate card has actual custom overrides (non-empty values that differ from standard)
 * A rate card is considered "custom" only if:
 * 1. It has a custom rate card ID, AND
 * 2. It has at least one value that differs from the standard rate card
 *
 * If all values have been reset, the rate card should show as "Standard" even if a custom ID exists
 */
export const hasCustomRateCard = (
  mergedRateCard: IMergedRateCard | null,
  standardRateCard?: IRateCard | null
): boolean => {
  // Must have a custom rate card ID
  if (!hasCustomRateCardId(mergedRateCard)) {
    return false;
  }

  // If no standard rate card provided, fall back to just checking if custom ID exists
  if (!standardRateCard) {
    return true;
  }

  // Check if any values in the merged card differ from the standard card
  const mergedValues = mergedRateCard?.values || {};
  const standardValues = standardRateCard.values || {};

  // Check if any value differs from standard
  for (const [key, mergedValue] of Object.entries(mergedValues)) {
    const standardValue = standardValues[key];
    if (!standardValue || standardValue.value !== mergedValue.value) {
      return true;
    }
  }

  // All values match standard - not truly custom
  return false;
};

/**
 * Identifies which rate values are custom (overridden)
 * Compares merged rate card values with standard rate card values
 * A value is custom if it differs from the standard rate card
 */
export const getCustomValueKeys = (
  mergedRateCard: IMergedRateCard,
  standardRateCard?: IRateCard | null
): string[] => {
  // No custom card exists - check both field names due to backend inconsistency
  if (!hasCustomRateCardId(mergedRateCard)) {
    return [];
  }

  // If no standard rate card provided, we can't determine which values are custom
  // Fall back to checking if values exist in merged that differ
  if (!standardRateCard) {
    // Return all keys in merged card as potentially custom
    return Object.keys(mergedRateCard.values);
  }

  // Compare merged values with standard values to find custom ones
  const customKeys: string[] = [];
  Object.entries(mergedRateCard.values).forEach(([key, mergedValue]) => {
    const standardValue = standardRateCard.values[key];
    // A value is custom if:
    // 1. It doesn't exist in the standard card, OR
    // 2. The value differs from the standard card
    if (!standardValue || standardValue.value !== mergedValue.value) {
      customKeys.push(key);
    }
  });

  return customKeys;
};

/**
 * Builds form default values from merged rate card
 */
export const buildEditFormDefaultValues = (
  mergedRateCard: IMergedRateCard
): RateCardEditFormData => {
  const values: Record<string, { value: number; isEdited: boolean }> = {};

  Object.entries(mergedRateCard.values).forEach(([key, rateValue]) => {
    values[key] = {
      value: rateValue.value,
      isEdited: false
    };
  });

  return { values, comment: "" };
};

/**
 * Converts rate card values to table row format
 */
export const convertToTableData = (
  values: Record<string, IRateValue>
): Array<{ data: IRateValue; id: string }> => {
  return Object.entries(values).map(([key, value]) => ({
    data: {
      ...value,
      key
    },
    id: key
  }));
};

/**
 * Builds rate card update request from form data
 * Only includes values that differ from the standard rate card
 * Returns keysToDelete for values that were reset to standard (should be removed from custom card)
 */
export const buildRateCardUpdateRequest = (
  formData: RateCardEditFormData,
  standardValues: Record<string, IRateValue>,
  currentCustomKeys: string[] = []
) => {
  const values: Record<string, IRateValue> = {};
  const keysToDelete: string[] = [];

  // Guard against undefined/null form values
  if (!formData?.values) {
    return { values, keysToDelete };
  }

  // Process all form values
  Object.entries(formData.values).forEach(([key, formValue]) => {
    const standardValue = standardValues[key];

    // Only include if value differs from standard
    if (
      standardValue &&
      formValue.value !== undefined &&
      formValue.value !== null &&
      formValue.value !== standardValue.value
    ) {
      values[key] = {
        ...standardValue,
        value: formValue.value
      };
    } else if (currentCustomKeys.includes(key)) {
      // Value matches standard AND was previously a custom override
      // This means user reset it - should be deleted from custom card
      keysToDelete.push(key);
    }
  });

  return { values, keysToDelete };
};

/**
 * Builds rate card create request for override
 * Only includes overridden values (not all values from the original card)
 */
export const buildRateCardCreateRequest = (
  formData: RateCardEditFormData,
  originalRateCard: IMergedRateCard,
  userId: string,
  customName: string
) => {
  const values: Record<string, IRateValue> = {};

  // Guard against undefined/null values
  if (!formData?.values || !originalRateCard?.values) {
    return {
      userId,
      name: customName,
      description: originalRateCard?.description,
      values
    };
  }

  // Only include values that have been changed/overridden
  Object.entries(originalRateCard.values).forEach(([key, originalValue]) => {
    const formValue = formData.values[key];

    // Check if value has actually changed from original (not relying on isEdited flag)
    const hasValueChanged =
      formValue &&
      formValue.value !== undefined &&
      formValue.value !== null &&
      formValue.value !== originalValue.value;

    if (hasValueChanged) {
      values[key] = {
        ...originalValue,
        value: formValue.value
      };
    }
    // Don't include unchanged values - custom card only stores overrides
  });

  return {
    userId,
    name: customName,
    description: originalRateCard.description,
    values
  };
};

/**
 * Generates rate card name for override
 */
export const generateOverrideRateCardName = (
  engagementName: string,
  contractorName: string
): string => {
  const timestamp = new Date().getTime();
  return `${engagementName} - ${contractorName} (Custom ${timestamp})`;
};

/**
 * Checks if any values have been edited in the form
 */
export const hasEditedValues = (formData: RateCardEditFormData): boolean => {
  if (!formData?.values) {
    return false;
  }
  return Object.values(formData.values).some(value => value.isEdited);
};
